/**
 * Optional sample pack.
 *
 * The synthesised sounds are deliberately the *fallback*, not the ceiling: a decent recorded chime beats
 * anything an oscillator will do for a reward, and a real engine loop beats a filtered sawtooth. But
 * audio files have to come from somewhere with a licence, and nothing here ships with the repository -
 * so this module looks for a pack and quietly does without if there is not one.
 *
 * Drop CC0/public-domain WAV or OGG files into `public/content/audio/` using the names in `SAMPLES`
 * below. Anything present is used; anything missing falls back to the synth. There is no configuration
 * and no manifest to keep in step - the filename *is* the wiring.
 *
 * Packs that fit this game and are CC0 (no attribution required, usable commercially):
 *
 *   - Kenney "Interface Sounds" and "Impact Sounds" - kenney.nl/assets  (chimes, taps, thuds)
 *   - "512 Sound Effects" by SubspaceAudio - opengameart.org            (pickups, fanfares)
 *   - Freesound.org, filtered to the CC0 licence                        (engine loops, splashes)
 *
 * Check the licence yourself before shipping anything: this list is a starting point, not a warranty.
 */

/**
 * Every sound the game will use a sample for if one exists.
 *
 * `loop: true` means it is a bed that runs continuously and needs to loop seamlessly; the rest are
 * one-shots. Extensions are tried in order, so either WAV or OGG works.
 */
export const SAMPLES = {
  engine: { loop: true },
  tyres: { loop: true },
  /**
   * The emergency lights' siren. A bed, not a sting: it runs for as long as she holds the horn down.
   *
   * Which is why it sits up here with the engine rather than with the one-shots below - the same
   * zero-crossing warning applies, and a clicking siren loop is worse than the synthesised one.
   */
  siren: { loop: true },
  collect: { loop: false },
  fanfare: { loop: false },
  splash: { loop: false },
  thump: { loop: false },
  horn: { loop: false },
  crayon: { loop: false },
  bark: { loop: false },
  /**
   * The press of a menu button.
   *
   * The one to be most careful with in a pack: it plays on every press of every button, so a sample with
   * a note or a tail in it turns drumming on the Garage into a tune. Kenney's "Interface Sounds" has
   * several dry taps that fit; anything longer than about 80ms does not.
   */
  press: { loop: false },
} as const;

export type SampleName = keyof typeof SAMPLES;

const EXTENSIONS = ['ogg', 'wav', 'mp3'] as const;

// BASE_URL rather than a bare './': a relative URL resolves against the document, so it breaks
// when the page is served one level up (/pawp instead of /pawp/). Vite substitutes the configured
// base, which is still './' for a LAN build.
export function sampleUrl(name: SampleName, extension: string): string {
  return `${import.meta.env.BASE_URL}content/audio/${name}.${extension}`;
}

/**
 * Try to fetch and decode one sample, returning null if there simply isn't one.
 *
 * A missing file is the *expected* case, not an error, so nothing is logged for a 404 - otherwise a
 * game with no pack installed would fill the console with nine warnings on every boot.
 */
export async function loadSample(
  context: AudioContext,
  name: SampleName,
): Promise<AudioBuffer | null> {
  for (const extension of EXTENSIONS) {
    try {
      const response = await fetch(sampleUrl(name, extension));
      if (!response.ok) continue;
      return await context.decodeAudioData(await response.arrayBuffer());
    } catch {
      // Wrong format, corrupt file, or no file. Try the next extension, then give up quietly.
    }
  }
  return null;
}

/**
 * Where "there is no pack" is remembered between launches.
 *
 * Eleven names by three extensions is over thirty fetches, all of them 404s, and they land at the worst
 * possible moment: the pack is loaded on the first press, the same press that unlocks the audio context
 * and is meant to answer with a bark. Every one of them goes through the service worker. No pack is the
 * normal case and it stays true for months at a time, so it is worth remembering.
 *
 * Escape hatches, because a remembered "no" that cannot be taken back is a trap for whoever drops files
 * in later: never remembered in dev, and `?audio=recheck` on the URL forgets it. Both are written down in
 * `public/content/audio/README.md`, next to the files themselves.
 */
const NO_PACK_KEY = 'pawp:no-audio-pack';

function noPackRemembered(): boolean {
  if (import.meta.env.DEV) return false;
  try {
    if (new URLSearchParams(location.search).get('audio') === 'recheck') {
      localStorage.removeItem(NO_PACK_KEY);
      return false;
    }
    return localStorage.getItem(NO_PACK_KEY) === '1';
  } catch {
    // Private mode, or no storage at all. Fetching is the safe answer.
    return false;
  }
}

function rememberNoPack(empty: boolean): void {
  if (import.meta.env.DEV) return;
  try {
    if (empty) localStorage.setItem(NO_PACK_KEY, '1');
    else localStorage.removeItem(NO_PACK_KEY);
  } catch {
    // Nothing to do: the only cost is asking again next time.
  }
}

/** Load whatever the pack happens to contain. Absent entries stay absent. */
export async function loadSamplePack(
  context: AudioContext,
): Promise<Partial<Record<SampleName, AudioBuffer>>> {
  if (noPackRemembered()) return {};

  const names = Object.keys(SAMPLES) as SampleName[];
  const loaded = await Promise.all(names.map((name) => loadSample(context, name)));

  const pack: Partial<Record<SampleName, AudioBuffer>> = {};
  for (const [index, buffer] of loaded.entries()) {
    const name = names[index];
    if (name !== undefined && buffer !== null) pack[name] = buffer;
  }
  rememberNoPack(Object.keys(pack).length === 0);
  return pack;
}
